import {
  Crockford32Options,
  crockford32StringFormat,
} from "@redotech/util/crockford32";
import z from "zod/v4";

export type AccountId = ID<"account">;
export const accountIdFormat = idFormat<AccountId>({ prefix: "acc_" });

export type ProductId = ID<"product">;
export const productIdFormat = idFormat<ProductId>({ prefix: "prd_" });

export type ProductImageId = ID<"productImage">;
export const productImageIdFormat = idFormat<ProductImageId>({
  prefix: "pri_",
});

export type ProductFamilyId = ID<"productFamily">;
export const productFamilyIdFormat = idFormat<ProductFamilyId>({
  prefix: "pfm_",
});

export type ProductFamilyImageId = ID<"productFamilyImage">;
export const productFamilyImageIdFormat = idFormat<ProductFamilyImageId>({
  prefix: "pfi_",
});

export type ProductFamilyOptionId = ID<"productFamilyOption">;
export const productFamilyOptionIdFormat = idFormat<ProductFamilyOptionId>({
  prefix: "pfo_",
});

export type ProductFamilyOptionValueId = ID<"productFamilyOptionValue">;
export const productFamilyOptionValueIdFormat =
  idFormat<ProductFamilyOptionValueId>({ prefix: "pfov_" });

export type LocationId = ID<"location">;
export const locationIdFormat = idFormat<LocationId>({ prefix: "loc_" });

export type StorageLocationId = ID<"storageLocation">;
export const storageLocationIdFormat = idFormat<StorageLocationId>({
  prefix: "sl_",
});

export type StorageLocationInventoryId = ID<"storageLocationInventory">;
export const storageLocationInventoryIdFormat =
  idFormat<StorageLocationInventoryId>({ prefix: "sli_" });

export type StorageLocationAdjustmentId = ID<"storageLocationAdjustment">;
export const storageLocationAdjustmentIdFormat =
  idFormat<StorageLocationAdjustmentId>({ prefix: "sla_" });

export type PricingRuleId = ID<"pricingRule">;
export const pricingRuleIdFormat = idFormat<PricingRuleId>({ prefix: "prl_" });

export type AdjustmentSessionId = ID<"inventoryAdjustmentSession">;
export const adjustmentSessionIdFormat = idFormat<AdjustmentSessionId>({
  prefix: "adj_",
});

export type ForecastId = ID<"forecast">;
export const forecastIdFormat = idFormat<ForecastId>({ prefix: "fcst_" });

export type SupplierId = ID<"supplier">;
export const supplierIdFormat = idFormat<SupplierId>({ prefix: "sup_" });

export type PurchaseOrderId = ID<"purchaseOrder">;
export const purchaseOrderIdFormat = idFormat<PurchaseOrderId>({
  prefix: "po_",
});

export type PurchaseOrderProductId = ID<"purchaseOrderProduct">;
export const purchaseOrderProductIdFormat = idFormat<PurchaseOrderProductId>({
  prefix: "pop_",
});

export type InboundShipmentId = ID<"inboundShipment">;
export const inboundShipmentIdFormat = idFormat<InboundShipmentId>({
  prefix: "inb_",
});

export type PurchaseOrderFileId = ID<"purchaseOrderFile">;
export const purchaseOrderFileIdFormat = idFormat<PurchaseOrderFileId>({
  prefix: "pof_",
});

export type ImageId = ID<"image">;
export const imageIdFormat = idFormat<ImageId>({ prefix: "img_" });

export type AttributeDefinitionId = ID<"attributeDefinition">;
export const attributeDefinitionIdFormat = idFormat<AttributeDefinitionId>({
  prefix: "attr_",
});

export type CheckoutSessionId = ID<"checkoutSession">;
export const checkoutSessionIdFormat = idFormat<CheckoutSessionId>({
  prefix: "chs_",
});

export type CheckoutSessionItemId = ID<"checkoutSessionItem">;
export const checkoutSessionItemIdFormat = idFormat<CheckoutSessionItemId>({
  prefix: "chi_",
});

export type CheckoutFulfillmentOptionId = ID<"checkoutFulfillmentOption">;
export const checkoutFulfillmentOptionIdFormat =
  idFormat<CheckoutFulfillmentOptionId>({ prefix: "cfo_" });

export type CheckoutFulfillmentGroupId = ID<"checkoutFulfillmentGroup">;
export const checkoutFulfillmentGroupIdFormat =
  idFormat<CheckoutFulfillmentGroupId>({ prefix: "cfg_" });

export type CheckoutOrderId = ID<"checkoutOrder">;
export const checkoutOrderIdFormat = idFormat<CheckoutOrderId>({
  prefix: "cho_",
});

export type ProductCategoryId = ID<"productCategory">;
export const productCategoryIdFormat = idFormat<ProductCategoryId>({
  prefix: "pcat_",
});

export type HsCodeId = ID<"hsCode">;
export const hsCodeIdFormat = idFormat<HsCodeId>({ prefix: "hsc_" });

export type InboundShipmentProductId = ID<"inboundShipmentProduct">;
export const inboundShipmentProductIdFormat =
  idFormat<InboundShipmentProductId>({ prefix: "isp_" });

export type InboundShipmentFileId = ID<"inboundShipmentFile">;
export const inboundShipmentFileIdFormat = idFormat<InboundShipmentFileId>({
  prefix: "isf_",
});

export type ShipmentDisputeId = ID<"shipmentDispute">;
export const shipmentDisputeIdFormat = idFormat<ShipmentDisputeId>({
  prefix: "shd_",
});

export type ShipmentDisputePhotoId = ID<"shipmentDisputePhoto">;
export const shipmentDisputePhotoIdFormat = idFormat<ShipmentDisputePhotoId>({
  prefix: "sdp_",
});

export type ReturnsCenterId = ID<"returnsCenter">;
export const returnsCenterIdFormat = idFormat<ReturnsCenterId>({
  prefix: "rctr_",
});

export type ShopifyListingId = ID<"shopifyListing">;
export const shopifyListingIdFormat = idFormat<ShopifyListingId>({
  prefix: "sfl_",
});

export type ShopifyPublicationId = ID<"shopifyPublication">;
export const shopifyPublicationIdFormat = idFormat<ShopifyPublicationId>({
  prefix: "spub_",
});

export type ChatGptListingId = ID<"chatGptListing">;
export const chatGptListingIdFormat = idFormat<ChatGptListingId>({
  prefix: "cgpl_",
});

export type RedoMarketplaceListingId = ID<"redoMarketplaceListing">;
export const redoMarketplaceListingIdFormat =
  idFormat<RedoMarketplaceListingId>({ prefix: "rml_" });

export type EbayListingId = ID<"ebayListing">;
export const ebayListingIdFormat = idFormat<EbayListingId>({ prefix: "ebl_" });

export type EbayProductListingId = ID<"ebayProductListing">;
export const ebayProductListingIdFormat = idFormat<EbayProductListingId>({
  prefix: "epl_",
});

export type GoogleShoppingListingId = ID<"googleShoppingListing">;
export const googleShoppingListingIdFormat = idFormat<GoogleShoppingListingId>({
  prefix: "gsl_",
});

export type ProcessingSessionId = ID<"processingSession">;
export const processingSessionIdFormat = idFormat<ProcessingSessionId>({
  prefix: "rps_",
});

export type ProcessingSessionItemId = ID<"processingSessionItem">;
export const processingSessionItemIdFormat = idFormat<ProcessingSessionItemId>({
  prefix: "rpsi_",
});

type ID<T> = string & { __idType: T };

export type Crockford32Format<T extends string> = {
  read(value: T): bigint;
  write(value: bigint): T;
  test(value: string): value is T;
  schema: z.ZodType<T, string>;
  readonly prefix: string;
};

function idFormat<T extends string>({
  prefix = "",
}: Crockford32Options = {}): Crockford32Format<T> {
  const { read, write } = crockford32StringFormat({ prefix });
  const result: Crockford32Format<T> = {
    get prefix() {
      return prefix;
    },
    read(value: T): bigint {
      return read(value);
    },
    write(value: bigint): T {
      return write(value) as T;
    },
    test(value: string): value is T {
      return !prefix || value.startsWith(prefix);
    },
    schema: z
      .custom<T>((value) => {
        return (
          typeof value === "bigint" ||
          (typeof value === "string" && value.startsWith(prefix))
        );
      })
      .transform((value) =>
        typeof value === "bigint" ? (write(value) as T) : value,
      )
      .meta({ annotation: "String" }) as z.ZodType<T, string>,
  };
  return result;
}
